import { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { SOATable } from './Components/SOATable';
import { PropertySelector } from './Components/PropertySelector';
import { GenerateSOAModal } from './Components/GenerateSOAModal';
import { DeleteSOAConfirmationModal } from './Components/DeleteSOAConfirmationModal';
import {
  defaultSOA,
  getSOAInitialValues,
  StatementOfAccountValidationSchema,
  autoFillPropertyDetails,
} from './Components/FormikHelpers';
import { getPropertyList } from '../../../Inventory/pages/FacilityInventory/Helpers/Api';
import { validateStep } from './Components/FormikHelpers/ValidationSchema';
import { getSOAList, createSOA, updateSOA, deleteSOA, resendApprovalLinks, getCompanyByPropertyId } from '../api';
import { getAllApprovers } from '../../Approver/Helpers/Api';
import './soa.scss';

const SOA_DRAFT_KEY = 'soa-generate-draft-v1';

const readSOADraft = () => {
  try {
    const raw = window.localStorage.getItem(SOA_DRAFT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clearSOADraft = () => {
  try {
    window.localStorage.removeItem(SOA_DRAFT_KEY);
  } catch {
    /* ignore */
  }
};

const GenerateStatementOfAccount = () => {
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [soas, setSOAs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedSOA, setSelectedSOA] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState({
    property_id: '',
    building_name: '',
    unit_no: '',
    status: 'pending',
  });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    const user = getUserFromToken();
    if (!user) return;

    setUser(user);

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await getPropertyList(user?.role || '', user?.id || '');
      setProperties(result?.propertyList || []);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        html: '<p class="text-lg">We couldn\'t fetch the properties. Please try again later!</p>',
        confirmButtonColor: '#3085d6',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, getUserFromToken]);

  const fetchSOAs = useCallback(
    async (propertyId, buildingName, status, search) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const { data } = await getSOAList({ propertyId, buildingName, status, search: search || undefined });
        setSOAs(data || []);
      } catch (error) {
        setSOAs([]);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          html: `<p class="text-lg">${error.message || 'Failed to fetch Statements of Account. Please try again!'}</p>`,
          confirmButtonColor: '#f46a6a',
        });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [dispatch]
  );

  const handleResendApprovalLinks = useCallback(async () => {
    const currentSOAId = selectedSOA?._id;
    if (!currentSOAId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        html: '<p class="text-lg">No SOA selected for resending approval links.</p>',
        confirmButtonColor: '#f46a6a',
      });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await resendApprovalLinks(currentSOAId);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        html: `<p class="text-lg">${response.message || 'Approval links resent successfully!'}</p>`,
        confirmButtonColor: '#34c38f',
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        html: `<p class="text-lg">${error.message || 'Failed to resend approval links. Please try again!'}</p>`,
        confirmButtonColor: '#f46a6a',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, selectedSOA]);

  const handleSubmit = useCallback(
    async (values, { setSubmitting, resetForm, setTouched }) => {
      const isValid = await validateStep(StatementOfAccountValidationSchema, values);
      if (!isValid) {
        setTouched(
          Object.keys(values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        );
        setSubmitting(false);
        return;
      }

      if (!selectedSOA?._id) {
        const result = await Swal.fire({
          title: 'Are you sure?',
          text: 'Do you want to create this Statement of Account?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#34c38f',
          cancelButtonColor: '#f46a6a',
          confirmButtonText: 'Yes, create it!',
          cancelButtonText: 'No, cancel',
        });

        if (!result.isConfirmed) {
          setSubmitting(false);
          return;
        }
      }

      Swal.fire({
        title: selectedSOA?._id ? 'Updating SOA...' : 'Creating SOA...',
        html: 'Please wait while we process your request.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        let companyId = null;
        if (values.property_details?.property_id) {
          const companyResponse = await getCompanyByPropertyId(values.property_details.property_id);
          companyId = companyResponse?._id;
        }

        let updatedApprovals = values.approvals || [];
        if (companyId) {
          const approversResponse = await getAllApprovers(companyId);
          const approvers = approversResponse.approvers || [];
          updatedApprovals = approvers.map((approver) => {
            const existingApproval = (values.approvals || []).find((a) => a.email === approver.email);
            return existingApproval || {
              email: approver.email,
              approved: false,
              signature: '',
              approved_at: null,
            };
          });
        }

        if (updatedApprovals.length === 0) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            html: '<p class="text-lg">No approvers available for the selected property.</p>',
            confirmButtonColor: '#f46a6a',
          });
          setSubmitting(false);
          return;
        }

        const updatedValues = { ...values, approvals: updatedApprovals };
        const submitFunction = selectedSOA?._id ? updateSOA : createSOA;
        const submitData = selectedSOA?._id ? [selectedSOA._id, updatedValues] : [updatedValues];
        await submitFunction(...submitData);

        Swal.fire({
          icon: 'success',
          title: 'Hooray!',
          html: `<p class="text-lg">Statement of Account ${selectedSOA?._id ? 'updated' : 'created'} successfully! 🎉</p>`,
          confirmButtonColor: '#34c38f',
        });

        if (!selectedSOA) {
          resetForm({ values: defaultSOA });
          setSelectedProperty({ property_id: '', building_name: '', unit_no: '', status: 'all' });
          clearSOADraft();
        }

        setIsModalOpen(false);
        fetchSOAs(selectedProperty.property_id, selectedProperty.building_name, selectedProperty.status, debouncedSearch);
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Oh No!',
          html: `<p class="text-lg">Failed to ${selectedSOA?._id ? 'update' : 'create'} the Statement of Account.<br/>${error.message || 'Please try again.'}</p>`,
          confirmButtonColor: '#f46a6a',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [selectedSOA, selectedProperty, fetchSOAs, debouncedSearch]
  );

  const formik = useFormik({
    initialValues: defaultSOA,
    validationSchema: StatementOfAccountValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const forceSubmit = async (e) => {
    e?.preventDefault?.();
    await handleSubmit(formik.values, {
      setSubmitting: formik.setSubmitting,
      resetForm: formik.resetForm,
      setTouched: formik.setTouched,
    });
  };

  const loadInitialValues = useCallback(
    async (soaId) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const initialValues = await getSOAInitialValues(soaId);
        const hasValidPropertyDetails =
          initialValues.property_details &&
          initialValues.property_details.property_id &&
          properties.some((p) => p._id === initialValues.property_details.property_id);

        if (!hasValidPropertyDetails) {
          Swal.fire({
            icon: 'warning',
            title: 'Invalid Property',
            html: '<p class="text-lg">The selected SOA has no valid property associated. Please select a property manually.</p>',
            confirmButtonColor: '#3085d6',
          });
          formik.setValues({ ...initialValues, property_details: selectedProperty });
          return;
        }

        // Fetch approvers for the property's company
        let updatedValues = initialValues;
        if (initialValues.property_details?.property_id) {
          try {
            const companyResponse = await getCompanyByPropertyId(initialValues.property_details.property_id);
            const companyId = companyResponse?.id;
            if (companyId) {
              const approversResponse = await getAllApprovers(companyId);
              const approvers = approversResponse.data || [];
              updatedValues = {
                ...initialValues,
                approvals: approvers.map((approver) => {
                  const existingApproval = (initialValues.approvals || []).find((a) => a.email === approver.email);
                  return existingApproval || {
                    email: approver.email,
                    approved: false,
                    signature: '',
                    approved_at: null,
                  };
                }),
              };
            }
          } catch (error) {
            console.error('Failed to fetch approvers:', error);
            // Proceed with existing approvals or empty array
            updatedValues = { ...initialValues, approvals: initialValues.approvals || [] };
          }
        }

        formik.setValues(updatedValues);
        setSelectedProperty({
          property_id: initialValues.property_details.property_id || '',
          building_name: initialValues.property_details.building_name || '',
          unit_no: initialValues.property_details.unit_no || '',
          status: selectedProperty.status || 'all',
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Oops!',
          html: '<p class="text-lg">Failed to load SOA data. Please try again! 😕</p>',
          confirmButtonColor: '#f46a6a',
        });
        formik.resetForm({ values: { ...defaultSOA, property_details: selectedProperty } });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [formik, dispatch, selectedProperty, properties]
  );

  useEffect(() => {
    if (!isModalOpen || selectedSOA) return;
    try {
      window.localStorage.setItem(SOA_DRAFT_KEY, JSON.stringify(formik.values));
    } catch {
      /* ignore */
    }
  }, [formik.values, isModalOpen, selectedSOA]);

  useEffect(() => {
    const draft = readSOADraft();
    if (!draft) return;
    Swal.fire({
      icon: 'info',
      title: 'Restore unsaved draft?',
      html: '<p class="text-lg">We found a Statement of Account you were creating but didn\'t get to save. Restore it?</p>',
      showCancelButton: true,
      confirmButtonColor: '#34c38f',
      cancelButtonColor: '#f46a6a',
      confirmButtonText: 'Restore draft',
      cancelButtonText: 'Discard',
    }).then((res) => {
      if (res.isConfirmed) {
        formik.setValues(draft);
        if (draft.property_details) setSelectedProperty(draft.property_details);
        setSelectedSOA(null);
        setIsModalOpen(true);
      } else {
        clearSOADraft();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchSOAs(selectedProperty.property_id, selectedProperty.building_name, selectedProperty.status, debouncedSearch);
  }, [selectedProperty.property_id, selectedProperty.building_name, selectedProperty.status, debouncedSearch, fetchSOAs]);

  const handlePropertySelectionChange = useCallback(
    (newValues) => {
      setSelectedProperty(newValues);
      formik.setFieldValue('property_details', newValues);
    },
    [formik]
  );

  const handleAddClick = useCallback(async () => {
    try {
      const initialValues = {
        ...defaultSOA,
        property_details: {
          property_id: typeof selectedProperty.property_id === 'string' ? selectedProperty.property_id : '',
          building_name: typeof selectedProperty.building_name === 'string' ? selectedProperty.building_name : '',
          unit_no: typeof selectedProperty.unit_no === 'string' ? selectedProperty.unit_no : '',
          status: selectedProperty.status || 'all',
        },
      };

      let formValues = initialValues;
      if (selectedProperty.property_id) {
        setIsAutoFilling(true);
        formValues = await autoFillPropertyDetails(
          initialValues,
          selectedProperty.property_id,
          selectedProperty.building_name,
          selectedProperty.unit_no
        );

        // Fetch approvers for the property's company
        try {
          const companyResponse = await getCompanyByPropertyId(selectedProperty.property_id);
          const companyId = companyResponse?.id;
          if (companyId) {
            const approversResponse = await getAllApprovers(companyId);
            const approvers = approversResponse.data || [];
            formValues = {
              ...formValues,
              approvals: approvers.map((approver) => ({
                email: approver.email,
                approved: false,
                signature: '',
                approved_at: null,
              })),
            };
          }
        } catch (error) {
          console.error('Failed to fetch approvers:', error);
          formValues = { ...formValues, approvals: [] };
        }
      }

      await formik.setValues(formValues);
      setSelectedSOA(null);
      setIsModalOpen(true);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Oops!',
        html: '<p class="text-lg">Failed to initialize SOA form. Please try again! 😔</p>',
        confirmButtonColor: '#f46a6a',
      });
    } finally {
      setIsAutoFilling(false);
    }
  }, [selectedProperty, formik]);

  const handleEditClick = useCallback(
    async (soa) => {
      try {
        setSelectedSOA(soa);
        await loadInitialValues(soa._id);
        setIsModalOpen(true);
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Oops!',
          html: '<p class="text-lg">Failed to load SOA data. Please try again! 😕</p>',
          confirmButtonColor: '#f46a6a',
        });
      }
    },
    [loadInitialValues]
  );

  const handleDeleteClick = (soa) => {
    setSelectedSOA(soa);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await deleteSOA(selectedSOA._id);
      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Statement of Account deleted successfully!',
      });
      fetchSOAs(selectedProperty.property_id, selectedProperty.building_name, selectedProperty.status, debouncedSearch);
      setIsDeleteModalOpen(false);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message || 'Failed to delete SOA. Please try again!' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
      setSelectedSOA(null);
    }
  };

  const handleModalOpenChange = useCallback(
    (open) => {
      setIsModalOpen(open);
      if (!open && !selectedSOA) clearSOADraft();
    },
    [selectedSOA]
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedSOA(null);
    formik.resetForm({ values: defaultSOA });
    setSelectedProperty({ property_id: '', building_name: '', unit_no: '', status: 'all' });
  };

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Statement of Account</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs
          title="Property Details"
          breadcrumbItem={selectedSOA ? 'Edit SOA' : 'Generate SOA'}
        />
        <PropertySelector
          propertyList={properties}
          selectedProperty={selectedProperty}
          onPropertySelectionChange={handlePropertySelectionChange}
          onAddClick={handleAddClick}
          isLoading={isAutoFilling}
          user={user}
          searchValue={search}
          onSearchChange={setSearch}
        />
        <SOATable
          items={soas}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          user={user}
        />
        <GenerateSOAModal
          formik={formik}
          isModalOpen={isModalOpen}
          setIsModalOpen={handleModalOpenChange}
          selectedSOA={!!selectedSOA}
          soaId={selectedSOA?._id}
          handleResendApprovalLinks={handleResendApprovalLinks}
          handleSubmit={forceSubmit}
          user={user}
        />
        <DeleteSOAConfirmationModal
          open={isDeleteModalOpen}
          onClose={closeModal}
          onDelete={handleDeleteConfirm}
          soa={selectedSOA}
          user={user}
        />
      </div>
    </div>
  );
};

export { GenerateStatementOfAccount };